import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Dimmer, Loader, Message } from 'semantic-ui-react';
import { connect } from 'redux-scaffolding-ts';
import './add-expertise-popup.less';
import { MachineListItemViewModel } from 'widgets/items-list/machine-list-item-deprecated';
import { InstructorExpertiseRequestFormStore } from 'stores/skills/instructor-expertise-request-form-store';
import { UserDto } from 'stores/users/users-store';

type UserDtoOrStringArray = UserDto | string[];

export interface TailwindAddExpertisePopupProps extends WithTranslation {
  instructorExpertiseRequestFormStore?: InstructorExpertiseRequestFormStore;
  machines: MachineListItemViewModel[];
  instructorIds: UserDtoOrStringArray;
  eventTypeId: string;
  originalEventTypeId: string;
  roleId: string;
  trainingLevelId: string;
  onCancel?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
}

interface TailwindAddExpertisePopupState {
  loading: boolean;
  errMsgs: string[];
}

@connect(['instructorExpertiseRequestFormStore', InstructorExpertiseRequestFormStore])
class TailwindAddExpertisePopup extends React.Component<TailwindAddExpertisePopupProps, TailwindAddExpertisePopupState> {
  static defaultProps = { onCancel: () => {}, onSuccess: () => {}, onError: () => {}, cancelRequest: false };
  state: TailwindAddExpertisePopupState = { loading: false, errMsgs: [] };

  private onCancel = () => {
    this.setState({ errMsgs: [] });
    this.props.onCancel();
  };

  private onCreateInstructorExpertiseRequest = () => {
    const errMsgs = [];
    const {
      onSuccess,
      onError,
      instructorExpertiseRequestFormStore,
      machines,
      instructorIds,
      eventTypeId,
      originalEventTypeId,
      roleId,
      trainingLevelId
    } = this.props;

    this.setState({ loading: true });
    const instructors = this.handleInstructores(instructorIds);
    instructorExpertiseRequestFormStore
      .createInstructorExpertiseRequest(instructors, machines, eventTypeId, roleId, trainingLevelId, originalEventTypeId)
      .then(response => {
        if (response?.isSuccess) onSuccess();
        else {
          const responseMessages = response?.messages || [];
          const responseErrorMsg =
            responseMessages.length > 0
              ? responseMessages
                  .map(msg => {
                    if (typeof msg === 'string') return msg;
                    if (msg?.body) return msg.body;
                    return JSON.stringify(msg);
                  })
                  .join(', ')
              : 'An error occurred';

          errMsgs.push(responseErrorMsg);

          this.setState({
            errMsgs
          });

          onError();
        }
      })
      .catch(error => {
        console.error(error);

        const errorMessages = error.response?.data?.messages || [];
        const errorMsg =
          errorMessages.length > 0
            ? errorMessages
                .map(msg => {
                  if (typeof msg === 'string') return msg;
                  if (msg?.body) return msg.body;
                  return JSON.stringify(msg);
                })
                .join(', ')
            : error.message || 'An error occurred';

        errMsgs.push(errorMsg);

        this.setState({
          errMsgs
        });
        onError();
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  private handleInstructores(input: UserDtoOrStringArray) {
    if (Array.isArray(input)) {
      return input;
    } else {
      return [input.id];
    }
  }

  public render() {
    const { t, instructorIds } = this.props;
    const { loading, errMsgs } = this.state;

    const errors = errMsgs.length ? (
      <Message
        className="error-message__style"
        error
        icon="exclamation circle"
        onDismiss={() => {}}
        header={t('An error ocurred')}
        list={this.props.instructorExpertiseRequestFormStore.state.result.messages.map(o => o.body)}
      />
    ) : null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div className="flex items-center justify-end min-h-screen p-4 text-center sm:p-0 mr-4">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={this.onCancel}>
            <Dimmer active={loading} style={{ background: 'rgba(0, 0, 0, 0.4)' }}>
              <Loader indeterminate>{t('')}</Loader>
            </Dimmer>
          </div>
          <div className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 max-w-sm w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg
                    className="h-6 w-6 text-black-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    {t('Add Expertise')}
                  </h3>
                  <div className="mt-2">
                    {errors}
                    {errMsgs.length > 0 && (
                      <p className="text-sm text-red-500">
                        {t(
                          `Defined expertise for ${
                            !Array.isArray(instructorIds)
                              ? `instructor ${instructorIds.firstName} ${instructorIds.lastName}`
                              : 'instructors'
                          } cannot be added to the master data.`
                        )}
                      </p>
                    )}
                    {errMsgs.length <= 0 && (
                      <p className="text-sm text-gray-500">
                        {t(
                          `Would you like to add defined expertise for ${
                            !Array.isArray(instructorIds)
                              ? `instructor ${instructorIds.firstName} ${instructorIds.lastName}`
                              : 'instructors'
                          } to the master data?`
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={this.onCreateInstructorExpertiseRequest}
              >
                {t('Yes')}
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={this.onCancel}
              >
                {errMsgs.length > 0 ? t('Close') : t('No')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withTranslation()(TailwindAddExpertisePopup);
